"use client";

// Shared Callback URL field — a read-only URL box with an inline copy icon
// (no separate Copy button), used by both the Add MCP modal and the Edit MCP
// drawer via McpConnectionFormFields. Owns its own copied state + the
// success-icon animation.

import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import { domId } from "@/lib/dom-id";

const labelClass = "block text-base font-medium leading-[1.4] text-[#0B0809]";

export function CallbackUrlField({
  purpose,
  url,
}: {
  purpose: string;
  url: string;
}) {
  const t = useTranslations("Mcp.addModal");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    if (!url) return;
    void navigator.clipboard?.writeText(url);
    setCopied(true);
  };

  const label = copied ? t("oauth.copied") : t("oauth.copy");

  return (
    <div className="flex flex-col gap-2">
      <span className={labelClass}>{t("oauth.callbackLabel")}</span>
      <div className="relative flex h-10 w-full items-center rounded-lg border border-[#DCDFE3] bg-[#F4F4F4] px-4 pr-10">
        <span
          data-testid={domId("sec", `${purpose}-callback-url`)}
          className="truncate text-sm leading-[1.4] text-[#8E9BA8]"
        >
          {url}
        </span>
        <button
          type="button"
          aria-label={label}
          title={label}
          data-testid={domId("btn", `${purpose}-copy-callback`)}
          onClick={handleCopy}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-lg text-[#6D7E91] transition-colors hover:bg-gray-100"
        >
          {copied ? (
            <Check
              key="copied"
              size={14}
              aria-hidden="true"
              className="animate-scale-in text-emerald-600"
            />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
